/**
 * SEO Meta Tags Utility
 * Dynamically sets page-specific meta tags for better search engine visibility
 */

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  imageAlt?: string;
}

export const setSEO = (config: SEOConfig) => {
  // Set title
  document.title = config.title;

  // Update or create description meta tag
  let descriptionTag = document.querySelector('meta[name="description"]');
  if (!descriptionTag) {
    descriptionTag = document.createElement('meta');
    descriptionTag.setAttribute('name', 'description');
    document.head.appendChild(descriptionTag);
  }
  descriptionTag.setAttribute('content', config.description);

  // Update or create keywords meta tag
  if (config.keywords) {
    let keywordsTag = document.querySelector('meta[name="keywords"]');
    if (!keywordsTag) {
      keywordsTag = document.createElement('meta');
      keywordsTag.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsTag);
    }
    keywordsTag.setAttribute('content', config.keywords);
  }

  // Update canonical URL
  const canonicalUrl = config.canonicalUrl || window.location.href;
  let canonicalTag = document.querySelector('link[rel="canonical"]');
  if (!canonicalTag) {
    canonicalTag = document.createElement('link');
    canonicalTag.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalTag);
  }
  canonicalTag.setAttribute('href', canonicalUrl);

  // Update Open Graph tags
  const ogImage = config.ogImage || 'https://blueglobemedia.com/og-image.jpg';
  const ogType = config.ogType || 'website';

  updateMetaTag('og:title', config.title);
  updateMetaTag('og:description', config.description);
  updateMetaTag('og:image', ogImage);
  updateMetaTag('og:type', ogType);
  updateMetaTag('og:url', canonicalUrl);

  // Update Twitter Card tags
  updateMetaTag('twitter:title', config.title);
  updateMetaTag('twitter:description', config.description);
  updateMetaTag('twitter:image', ogImage);
};

const updateMetaTag = (property: string, content: string) => {
  let tag = document.querySelector(`meta[property="${property}"], meta[name="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(property.includes('og:') || property.includes('twitter:') ? 'property' : 'name', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

// Page-specific SEO configurations
export const pageConfigs = {
  home: {
    title: 'Blue Globe Media | Digital Marketing Agency — SEO, Performance Marketing & Media Buying India',
    description: 'Blue Globe Media is a professional digital marketing agency helping businesses grow online through SEO, performance marketing, social media, media buying, paid advertising, branding and website development.',
    keywords: 'digital marketing agency India, SEO agency, performance marketing, media buying, social media marketing, paid advertising agency, branding agency, website development',
  },
  about: {
    title: 'About Blue Globe Media | Digital Marketing Agency & Services India',
    description: 'Learn about Blue Globe Media — a leading digital marketing agency in India specializing in SEO, performance marketing, social media management, and brand growth strategies.',
    keywords: 'about blue globe media, digital marketing agency India, SEO experts, marketing agency India, branding services',
  },
  services: {
    title: 'Digital Marketing Services | SEO, Performance Marketing, Media Buying India',
    description: 'Explore Blue Globe Media services: SEO optimization, performance marketing, social media marketing, media buying, paid advertising, branding, and website development. Grow your business online.',
    keywords: 'digital marketing services, SEO services India, performance marketing, media buying agency, social media marketing, paid advertising, branding, website development',
  },
  industries: {
    title: 'Industry Solutions | Digital Marketing for Multiple Sectors India',
    description: 'Blue Globe Media delivers industry-specific digital marketing solutions for e-commerce, healthcare, finance, real estate, education, and technology sectors in India.',
    keywords: 'industry solutions, digital marketing sectors, e-commerce marketing, healthcare marketing, finance marketing, real estate digital marketing',
  },
  process: {
    title: 'Our Digital Marketing Process | Blue Globe Media Methodology',
    description: 'Discover our proven digital marketing process: strategy, implementation, optimization, and growth. We focus on measurable results and transparent reporting.',
    keywords: 'digital marketing process, marketing methodology, digital strategy, marketing implementation, performance optimization',
  },
  contact: {
    title: 'Contact Blue Globe Media | Digital Marketing Agency India',
    description: 'Get in touch with Blue Globe Media for your digital marketing needs. Based in Mumbai, we serve businesses across India. Let\'s discuss your growth strategy.',
    keywords: 'contact us, digital marketing contact, marketing agency India, get in touch, Blue Globe Media contact',
  },
  'privacy-policy': {
    title: 'Privacy Policy | Blue Globe Media',
    description: 'Read the Blue Globe Media privacy policy. Learn how we collect, use, store, and protect your personal information.',
    keywords: 'privacy policy, Blue Globe Media privacy, data protection',
  },
  terms: {
    title: 'Terms & Conditions | Blue Globe Media',
    description: 'Read the Blue Globe Media terms and conditions for using our website and digital marketing services.',
    keywords: 'terms and conditions, terms of use, Blue Globe Media legal',
  },
};
